import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useAutoFocus } from "@/hooks/useAutoFocus";
import { A11yValue } from "@/components/A11yValue";
import {
  getAccessibility,
  setAccessibility as persistAccessibility,
  getBestScore,
  getGamesPlayed,
} from "@/play/storage";
import { primeSpeech } from "@/play/speech";

/**
 * Settings — Accessibilità ON/OFF + statistiche. Persistenza localStorage.
 * La preferenza `ft_accessibility` abilita la voce narrante (Web Speech) nella
 * schermata di gioco (vedi Game.jsx / play/speech.js).
 */
export default function Settings() {
  const navigate = useNavigate();
  const headingRef = useAutoFocus();
  const [accessibility, setAccessibilityState] = useState(() => getAccessibility());
  const [best] = useState(() => getBestScore());
  const [games] = useState(() => getGamesPlayed());

  const toggleAccessibility = () => {
    setAccessibilityState((prev) => {
      const next = !prev;
      persistAccessibility(next);
      if (next) primeSpeech(); // sblocca l'audio iOS nel gesto utente
      return next;
    });
  };

  return (
    <main className="screen" data-testid="settings-screen">
      <div className="screen__header">
        <button
          type="button"
          className="back-btn"
          data-testid="settings-back-button"
          onClick={() => navigate("/")}
          aria-label="Torna alla schermata principale"
        >
          <ChevronLeft size={16} strokeWidth={2.5} aria-hidden="true" />
          Indietro
        </button>
        <h1 className="screen__title" tabIndex={-1} ref={headingRef}>
          Impostazioni
        </h1>
      </div>

      <h2 className="section-label">Accessibilità</h2>
      <div className="setrow">
        <div>
          <span className="setrow__label" id="acc-label">
            Modalità accessibilità
          </span>
          <span className="setrow__hint" id="acc-hint">
            Legge ad alta voce le domande e gli esiti. Prima di ogni partita c'è un conto alla
            rovescia di 5 secondi: se usi VoiceOver puoi disattivarlo in quei secondi per giocare
            con i normali tocchi.
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={accessibility}
          aria-labelledby="acc-label"
          aria-describedby="acc-hint"
          className="switch-btn"
          data-testid="settings-accessibility-switch"
          onClick={toggleAccessibility}
        >
          <span className="switch" data-on={accessibility} aria-hidden="true">
            <span className="switch__knob" />
          </span>
        </button>
      </div>

      <p className="doc__p" style={{ marginTop: "var(--sp-md)" }}>
        <Link to="/accessibilita" className="doc__link" data-testid="settings-accessibility-guide-link">
          Guida all'accessibilità (VoiceOver e TalkBack)
        </Link>
      </p>

      <h2 className="section-label">Statistiche</h2>
      <div className="setrow">
        <span className="setrow__label">Record</span>
        <A11yValue
          className="setrow__value"
          testId="settings-best-score"
          visual={best > 0 ? String(best) : "—"}
          spoken={best > 0 ? `Record: ${best}` : "Record: nessuno ancora"}
        />
      </div>
      <div className="setrow">
        <span className="setrow__label">Partite giocate</span>
        <A11yValue
          className="setrow__value"
          testId="settings-games-played"
          visual={games > 0 ? String(games) : "—"}
          spoken={games > 0 ? `Partite giocate: ${games}` : "Partite giocate: nessuna"}
        />
      </div>
    </main>
  );
}
